"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import {
  isRealCalendarDate,
  parsedDayMatchesText,
} from "@/lib/tool-kit/date-math/calendar-date";

interface DateParts {
  year: number;
  month: number;
  day: number;
}

// Citation styles print English month and weekday names whatever the page language,
// so these stay fixed rather than coming from the translated `ui` strings.
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const ISO_DATE_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

export function partsFromIso(value: string): DateParts | null {
  const match = ISO_DATE_REGEX.exec(value);
  if (match === null) {
    return null;
  }
  const year = Number.parseInt(match[1], 10);
  const month = Number.parseInt(match[2], 10);
  const day = Number.parseInt(match[3], 10);
  if (!isRealCalendarDate(year, month, day)) {
    return null;
  }
  return { year, month, day };
}

export function partsFromText(text: string): DateParts | null {
  const isoParts = partsFromIso(text.trim());
  if (isoParts !== null) {
    return isoParts;
  }
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  if (!parsedDayMatchesText(text, parsed.getDate())) {
    return null;
  }
  return {
    year: parsed.getFullYear(),
    month: parsed.getMonth() + 1,
    day: parsed.getDate(),
  };
}

function weekdayName({ year, month, day }: DateParts): string {
  // Build the Date in UTC from the parts so the weekday never shifts
  // across timezones.
  return WEEKDAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
}

function buildFormats(
  parts: DateParts,
  strings: typeof DATE_FORMAT_CONVERTER_STRINGS
): { label: string; value: string; note: string }[] {
  const { year, month, day } = parts;
  const monthName = MONTHS[month - 1];
  return [
    {
      label: strings.apaLabel,
      value: `${year}, ${monthName} ${day}`,
      note: strings.apaNote,
    },
    {
      label: strings.mlaLabel,
      value: `${day} ${monthName} ${year}`,
      note: strings.mlaNote,
    },
    {
      label: strings.chicagoLabel,
      value: `${monthName} ${day}, ${year}`,
      note: strings.chicagoNote,
    },
    {
      label: strings.isoLabel,
      value: `${year}-${pad2(month)}-${pad2(day)}`,
      note: strings.isoNote,
    },
    {
      label: strings.usLabel,
      value: `${pad2(month)}/${pad2(day)}/${year}`,
      note: strings.usNote,
    },
    {
      label: strings.europeanLabel,
      value: `${pad2(day)}/${pad2(month)}/${year}`,
      note: strings.europeanNote,
    },
    {
      label: strings.longFormLabel,
      value: `${weekdayName(parts)}, ${monthName} ${day}, ${year}`,
      note: strings.longFormNote,
    },
  ];
}

/** English strings. A translated content file overrides these through `ui`. */
export const DATE_FORMAT_CONVERTER_STRINGS = {
  pickADate: "Pick a date",
  orTypeADate: "Or type a date (e.g. July 28, 2026)",
  typeADatePlaceholder: "July 28, 2026",
  couldNotUnderstand:
    "Could not understand that date. Try a form like July 28, 2026 or 2026-07-28.",
  clear: "Clear",
  everyFormat: "Every format",
  copied: "Copied!",
  copy: "Copy",
  noDateOnYourSource: "No date on your source?",
  noDateExplanation:
    " Use n.d. (no date) instead of guessing. APA writes (n.d.) where the year would go; MLA and Chicago also accept n.d. in the date position.",
  apaLabel: "APA reference format",
  apaNote: "Used inside parentheses in APA references: (2026, July 28)",
  mlaLabel: "MLA format",
  mlaNote: "Day-month-year, no commas — used in MLA works-cited entries",
  chicagoLabel: "Chicago format",
  chicagoNote:
    "Month-day-year — Chicago notes, bibliographies, and running text",
  isoLabel: "ISO 8601",
  isoNote: "International standard — filenames, spreadsheets, and data",
  usLabel: "US numeric",
  usNote: "Month/day/year — common in the United States",
  europeanLabel: "European numeric",
  europeanNote: "Day/month/year — common in Europe and most of the world",
  longFormLabel: "Long written form",
  longFormNote: "Full weekday form — letters, formal documents, cover pages",
} as const;

export default function DateFormatConverterWidget() {
  const [dateValue, setDateValue] = useState("");
  const [textValue, setTextValue] = useState("");
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const strings = useToolUi(DATE_FORMAT_CONVERTER_STRINGS);

  const parts =
    dateValue.length > 0
      ? partsFromIso(dateValue)
      : partsFromText(textValue.trim().length > 0 ? textValue : "");
  const formats = parts === null ? [] : buildFormats(parts, strings);
  const showParseError =
    dateValue.length === 0 && textValue.trim().length > 0 && parts === null;

  function handleCopy(value: string, index: number) {
    writeToClipboard(value).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  }

  function handleClear() {
    setDateValue("");
    setTextValue("");
    setCopiedIndex(null);
  }

  return (
    <>
      {/* Input */}
      <section className="space-y-4">
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="date-input"
          >
            {strings.pickADate}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-card p-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="date-input"
            onChange={(e) => {
              setDateValue(e.target.value);
              setCopiedIndex(null);
            }}
            type="date"
            value={dateValue}
          />
        </div>
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="date-text-input"
          >
            {strings.orTypeADate}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-card p-3 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="date-text-input"
            onChange={(e) => {
              setTextValue(e.target.value);
              setDateValue("");
              setCopiedIndex(null);
            }}
            placeholder={strings.typeADatePlaceholder}
            type="text"
            value={textValue}
          />
        </div>
        {showParseError && (
          <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm">
            {strings.couldNotUnderstand}
          </p>
        )}
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={dateValue.length === 0 && textValue.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </section>

      {/* Results */}
      {formats.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.everyFormat}
          </h2>
          <div className="space-y-3">
            {formats.map((format, index) => (
              <div
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-4"
                key={format.label}
              >
                <div className="min-w-0">
                  <p className="text-muted-foreground text-xs">
                    {format.label}
                  </p>
                  <p className="font-medium text-foreground text-lg">
                    {format.value}
                  </p>
                  <p className="mt-1 text-muted-foreground text-xs">
                    {format.note}
                  </p>
                </div>
                <button
                  className="rounded-lg border border-border bg-background px-4 py-1.5 font-medium text-foreground text-sm transition-colors hover:bg-muted"
                  onClick={() => handleCopy(format.value, index)}
                  type="button"
                >
                  {copiedIndex === index ? strings.copied : strings.copy}
                </button>
              </div>
            ))}
          </div>
          <p className="rounded-lg border border-border bg-muted/30 p-4 text-muted-foreground text-sm leading-relaxed">
            <span className="font-medium text-foreground">
              {strings.noDateOnYourSource}
            </span>
            {strings.noDateExplanation}
          </p>
        </section>
      )}
    </>
  );
}
